import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { useDateRange } from "@/dateRanges/DateRangeContext";
import { useThaloWeb } from "@/contexts/ThaloWebContext";
import { useQuery } from "@tanstack/react-query";
import getWeekends, { Weekends } from "@/chartOptions/getWeekends";
import { LocationData } from "@/location/location";
import { lineCharts } from "@/components/charts/api";
import { DateRange } from "@/dateRanges/dateRanges";

/**
 * Context type for chart options including temperature display and weekend highlighting
 */
type ChartOptionsContextType = {
  showTemperature: boolean;
  setShowTemperature: Dispatch<SetStateAction<boolean>>;
  temperature: any;
  highlightWeekends: boolean;
  setHighlightWeekends: Dispatch<SetStateAction<boolean>>;
  highlightPowerOff: boolean;
  setHighlightPowerOff: Dispatch<SetStateAction<boolean>>;
  weekends: Weekends;
  temperatureUnit: 'degF' | 'degC';
  setTemperatureUnit: Dispatch<SetStateAction<'degF' | 'degC'>>;
};

const ChartOptionsContext = createContext<ChartOptionsContextType | undefined>(undefined);

/**
 * Provider component for chart options that manages temperature display and weekend highlighting
 * 
 * @param props.children - Child components that will have access to the chart options
 * @param props.location - Optional location data to use for temperature queries. If not provided, uses selectedLocation from ThaloWebContext
 * @param props.customDateRange - Optional custom date range to use for temperature queries. If not provided, uses the default date range from useDateRange()
 * @param props.initialHighlightWeekends - Optional initial value for highlightWeekends
 * @param props.outdoorTemperatureUnit - Optional temperature unit to use for outdoor temperature queries. Default is 'degF'
 * 
 * @example
 * // Basic usage with default date range
 * <ChartOptionsContextProvider>
 *   <YourComponent />
 * </ChartOptionsContextProvider>
 * 
 * @example
 * // Usage with custom date range
 * <ChartOptionsContextProvider
 *   location={selectedLocation}
 *   customDateRange={{
 *     label: "Custom Range",
 *     value: {
 *       from: () => new Date(startDate),
 *       to: () => new Date(endDate),
 *       live: false
 *     }
 *   }}
 * >
 *   <YourComponent />
 * </ChartOptionsContextProvider>
 */
export const ChartOptionsContextProvider = ({ 
  children, 
  location,
  customDateRange,
  initialHighlightWeekends = true,
  outdoorTemperatureUnit = 'degF'
}: { 
  children: ReactNode, 
  location?: LocationData,
  customDateRange?: DateRange,
  initialHighlightWeekends?: boolean,
  outdoorTemperatureUnit?: 'degF' | 'degC'
}) => {
  const [showTemperature, setShowTemperature] = useState(false)
  const [highlightWeekends, setHighlightWeekends] = useState(false)  // Force to false
  const [highlightPowerOff, setHighlightPowerOff] = useState(false)
  const [temperatureUnit, setTemperatureUnit] = useState<'degF' | 'degC'>(outdoorTemperatureUnit)

  const { dateRange: defaultDateRange, windowSize } = useDateRange()
  const { selectedLocation } = useThaloWeb()

  const locationToUse = location || selectedLocation
  const dateRangeToUse = customDateRange || defaultDateRange

  const temperature = useQuery({
    ...lineCharts.detail(
      `locations/${locationToUse?.id}/temperature`,
      {
        dateRange: dateRangeToUse,
        windowSize,
        ...(outdoorTemperatureUnit === 'degC' && { target_unit: 'degC' }),
      }
    ),
    refetchInterval: false,
    enabled: showTemperature && !!locationToUse,
  })

  const weekends = getWeekends(dateRangeToUse)

  return (
    <ChartOptionsContext.Provider
      value={{
        showTemperature,
        setShowTemperature,
        temperature,
        highlightWeekends,
        setHighlightWeekends,
        highlightPowerOff,
        setHighlightPowerOff,
        weekends,
        temperatureUnit,
        setTemperatureUnit,
      }}
    >
      {children}
    </ChartOptionsContext.Provider>
  );
};

/**
 * Hook to access the chart options context
 * @throws Error if used outside of ChartOptionsContextProvider
 */
export const useChartOptions = () => {
  const context = useContext(ChartOptionsContext);
  if (context === undefined) {
    throw new Error("Context must be used within a Provider");
  }
  return context;
};
